import { AfterViewInit, ChangeDetectorRef, Component, ElementRef, OnDestroy, OnInit, ViewChild, ViewEncapsulation, ChangeDetectionStrategy } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { WindowState } from '@progress/kendo-angular-dialog';
import { ComboBoxComponent } from '@progress/kendo-angular-dropdowns';
import { orderBy } from '@progress/kendo-data-query';
import { ComponentBase } from '~/infrastructure/component-base';
import { LabelPreset } from '~/models/modules/labelPreset';
import { ReportsService } from '~/services/modules/reports/reports';
import { GridFieldsService } from '~/services/modules/stocks/gridField';
import { LabelService } from '~/services/modules/stocks/labels';
import { SharedService } from '~/services/sharedService';
import { ThemeManagerService } from '~/services/theme-manager';
import { LayoutManagerService } from '~/shared/components/layout/layout-manager';
import { LayoutComponent } from '~/shared/components/layout/layout.component';
import { PageState } from '~/shared/enums/stock-files.enum';
import { LabelType } from '~/shared/enums/label-type.enum';
import { ReportDesignerComponent } from '~/modules/stocks/reports/report/designer/reportdesigner.component';

@Component({
    selector: 'app-public-qr-upsert',
    templateUrl: './upsert.html',
    styleUrls: ['./upsert.scss'],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class PublicQrUpsertComponent extends ComponentBase implements OnInit, AfterViewInit, OnDestroy {

  private readonly _destroy$ = new Subject<void>();

  _pageState: PageState;
  _active: boolean;
  currentStep = 0;
  _theme: string;
  fields: any;
  selectedFields: any = [];
  _labelPreset: any[] = [];
  _labelPresetFiltered: any[] = [];
  _labelSize: any[] = [];
  _labelSizeFiltered: any[] = [];
  _model: any = {
    BarcodeValues: [],
    LabelSizeID: undefined,
    Row: 0,
    Column: 0,
    LabelPresetId: {},
    LabelSize: {},
    numberOfCopy: 1,
    name: '',
    isQr: true,
    isURL: false
  };
  _gridFields: any[] = [];
  _selectedgridFields: any[] = [];

  maximized: boolean;
  windowMode = true;
  windowState: WindowState = 'default';

  reportCategories: any[] = [];
  reportCategory: any;
  _previewDate: boolean = false;
  _previewObject: any;
  _customSize = ['Yes', 'No'];
  _isCustomSize: boolean = false;
  _gridFieldName: any;
  _showSave: boolean = false;
  _labelPresetObject: LabelPreset = new LabelPreset();

  accessFrom: string;
  isAsset: boolean = false;
  SharedModel: any;

  @ViewChild('mainWindow') kendoMainWindow: ElementRef;
  @ViewChild('kendo_input_Report_Name') name: ElementRef;
  @ViewChild('labelType') labelType: ComboBoxComponent;
  @ViewChild('custom_size') customSize: ComboBoxComponent;
  @ViewChild('labelSize') labelSize: ComboBoxComponent;
  @ViewChild('filterBy') filterBy: ComboBoxComponent;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private _themeManager: ThemeManagerService,
    private _sharedService: SharedService,
    private _layoutManager: LayoutManagerService,
    private _labelService: LabelService,
    private _gridFieldsService: GridFieldsService,
    private _reportService: ReportsService,
    private _layoutComponent: LayoutComponent,
    private cdr: ChangeDetectorRef
  ) {
    super();

    this.route.data.pipe(takeUntil(this._destroy$)).subscribe((data: any) => {
      this.accessFrom = data?.accessFrom;
      this.determineIsAsset();
    });

    this._themeManager.themeChange.pipe(takeUntil(this._destroy$)).subscribe((data) => {
      this._theme = data;
    });
  }

  determineIsAsset(): void {
    const isAssetFromRoute = this.accessFrom === 'assets';
    const sharedModel = this._sharedService?.model;
    const innerModel = sharedModel?.model;
    const isAssetFromModel = innerModel?.isAsset ?? sharedModel?.isAsset;
    const accessFromModel = innerModel?.accessFrom ?? sharedModel?.accessFrom;
    const isAssetFromUrl = !!(this.router?.url && this.router.url.includes('/asset'));

    this.isAsset = !!(isAssetFromRoute || isAssetFromModel || accessFromModel === 'assets' || isAssetFromUrl);
  }

  ngOnInit(): void {
    if (!this._labelPresetObject) {
      this._labelPresetObject = new LabelPreset();
    }
    this._getdata();
    this.determineIsAsset();
    this._loggedInSite();
    this._getLabelPreset();
    this._getReportCategories();
  }

  ngAfterViewInit(): void {
    setTimeout(() => {
      if (this._pageState !== PageState.EDIT) {
        this.name?.nativeElement?.focus();
        if (this.labelType) this.labelType.disabled = true;
        if (this.customSize) this.customSize.disabled = true;
        if (this.labelSize) this.labelSize.disabled = true;
      } else {
        if (this.labelType) this.labelType.disabled = false;
        if (this.customSize) {
          this.customSize.disabled = false;
          this.customSize.value = this._model.LabelSizeID ? 'No' : 'Yes';
        }
        if (this.labelSize) this.labelSize.disabled = false;
      }
    }, 200);
    this.cdr.detectChanges();
  }

  ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
    this._layoutManager.removeOverlay();
  }

  _getdata() {
    this.determineIsAsset();
    if (!this._labelPresetObject) {
      this._labelPresetObject = new LabelPreset();
    }
    this._pageState = this._sharedService.model && this._sharedService.model.pageState ? this._sharedService.model.pageState : null;
    this.SharedModel = this._sharedService.model && this._sharedService.model.model ? this._sharedService.model.model : null;
    this._active = true;

    if (this._pageState === PageState.EDIT && this.SharedModel?.labelPreset) {
      const preset = this.SharedModel.labelPreset;
      this._model.id = preset.id;
      this._model.name = preset.name;
      this._model.filterBy = preset.filterBy;
      this._model.isURL = !!preset.isURL;
      this._labelPresetObject.filterBy = this._model.filterBy;
      this._model.LabelSizeID = preset.labelSizeID;

      if (this._model.LabelSizeID) {
        if (this.customSize) this.customSize.value = 'No';
        this._isCustomSize = false;
        this._getLabelSize(this._model.LabelSizeID);
      } else {
        if (this.customSize) this.customSize.value = 'Yes';
        this._isCustomSize = true;
        this._model.Width = preset.width;
        this._model.Height = preset.height;
        this.SetCustomSizeAttributes(false);
      }

      this._showSave = true;
    } else if (this.SharedModel) {
      this._model.isURL = !!this.SharedModel.isURL;
      this._model.id = this.SharedModel.id;
    }

    if (this._pageState === PageState.ADD && this._model && this._model.id > 0) {
      this._model.id = null;
    }
  }

  _closeForm() {
    var closeComponent = {
      component: 'publicQr',
      isClosed: true
    };

    this._active = false;
    this.ngOnDestroy();
    this._sharedService.closeDialog.emit(closeComponent);
    this._sharedService.model = {};
  }

  _onStateChanges(e: any) {
    var element: any = this.kendoMainWindow;
    if (element && element.el && element.el.nativeElement) {
      if (e === 'minimized') {
        element.el.nativeElement.classList.remove('fullWidthFormForm');
      } else if (e === 'maximized') {
        element.el.nativeElement.classList.remove('fullWidthFormForm');
      } else if (e === 'default') {
        element.el.nativeElement.classList.add('fullWidthFormForm');
      }
    }
  }

  maximizeWindow() {
    this.windowState = 'maximized';
    this.maximized = true;
  }

  _getLabelPreset() {
    this._labelService.getSystemPresetLabel(this.isAsset, LabelType.PublicQr)
      .subscribe((x: any[]) => {
        if (this.isAsset && x) {
          x = x.filter((item: any) => (!item.customLabel && item.name != 'Asset with Name' && item.name != 'Asset with Model #') || item.customLabel);
        }
        this._labelPreset = x || [];
        this._labelPresetFiltered = x || [];

        if (this._pageState === PageState.EDIT && this.SharedModel?.labelPreset) {
          const preset = this.SharedModel.labelPreset;
          if (preset.customLabel) {
            this._model.LabelPresetId = this._labelPreset.find((p: any) => p.id == preset.relatedLabelPresetID) ||
              (this.SharedModel.labelPresets?.find((p: any) => p.id == preset.relatedLabelPresetID));
          } else {
            this._model.LabelPresetId = this._labelPreset.find((p: any) => p.id == preset.id) ||
              (this.SharedModel.labelPresets?.find((p: any) => p.id == preset.id));
          }
          if (!this._model.LabelPresetId && this.SharedModel.labelPresets) {
            this._model.LabelPresetId = this.SharedModel.labelPresets.find((p: any) => p.id == preset.relatedLabelPresetID || p.id == preset.id);
          }
          this._gridFieldName = this._model.LabelPresetId;
          this.getGridFieldsForEdit(this._model.LabelPresetId, preset.selectedFields);
        }
      });
  }

  _getLabelSize(labelSizeId?: any) {
    this._labelService.getLabelSizes()
      .subscribe((x: any[]) => {
        this._labelSize = x || [];
        this._labelSize = orderBy(this._labelSize, [{ field: 'name', dir: 'desc' }]);
        this._labelSizeFiltered = this._labelSize;
        if (labelSizeId) {
          const found = this._labelSize.find((s: any) => s.id == labelSizeId);
          if (found) {
            this._model.LabelSize = found;
            this._model.LabelSizeID = found.id;
            this._model.Row = found.rows;
            this._model.Column = found.columns;
            this._model.Width = found.width;
            this._model.Height = found.height;
          }
        }
        this._labelSizeFocus();
      });
  }

  _labeltypeFocus() {
    if (this.labelType) {
      this.labelType.focus();
      this.labelType.toggle();
    }
    if (this.labelSize) {
      this.labelSize.disabled = true;
    }
  }

  _labelSizeFocus() {
    if (this.labelSize) {
      this.labelSize.focus();
      this.labelSize.toggle();
    }
  }

  _handleLabelTypeFilter(value: any) {
    this._labelPresetFiltered = this._labelPreset.filter((s: any) => s.name.toLowerCase().indexOf(value.toLowerCase()) !== -1);
  }

  _handleLabelSizeFilter(value: any) {
    this._labelSizeFiltered = this._labelSize.filter((s: any) => s.name.toLowerCase().indexOf(value.toLowerCase()) !== -1);
  }

  _labelSizeSelectionChange(event: any) {
    if (event) {
      setTimeout(() => {
        this._model.LabelSize = event;
        this._model.Row = event.rows;
        this._model.Column = event.columns;
        this._model.LabelSizeID = event.id;
        this._model.Width = event.width;
        this._model.Height = event.height;
        this._getGridFields(this._gridFieldName);
        this.currentStep = 2;
      }, 200);
    }
  }

  _labelTypeSelectionChange(event: any) {
    if (event) {
      this._model.LabelPresetId = typeof event === 'object' ? event : (this._labelPreset.find(x => x.id === event) || event);
      this._gridFieldName = this._model.LabelPresetId;
      this._selectedgridFields = [];
      if (this.filterBy) this.filterBy.value = undefined;
      this._previewDate = false;
      this._previewObject = undefined;
      this._getGridFields(this._gridFieldName);

      setTimeout(() => {
        if (this.customSize) {
          this.customSize.disabled = false;
          this.customSize.focus();
          this.customSize.toggle();
        }
      }, 200);
    } else {
      this._selectedgridFields = [];
      if (this.filterBy) this.filterBy.value = undefined;
      this._previewDate = false;
      this._previewObject = undefined;
      this._showSave = false;
    }
  }

  GetSelectedGridFields(event: any) {
    this._selectedgridFields = event || [];
    this.currentStep = 3;

    if (this._selectedgridFields.length > 0) {
      if (this._isCustomSize) {
        if (this._model.Width && this._model.Height) {
          this._previewBarcode();
        }
      } else {
        this._previewBarcode();
      }
    }

    setTimeout(() => {
      if (this.filterBy) {
        this.filterBy.focus();
        this.filterBy.toggle();
      }
    }, 200);
  }

  _getGridFields(event: any) {
    let gridFields = '';
    if (!event) return;
    const name = typeof event === 'object' ? event.name : event;

    if (typeof event === 'object' && event.pageName) {
      gridFields = event.pageName;
    } else if (name === 'Location') {
      gridFields = 'locations';
    } else if (name === 'Stock Item' || name === 'Asset') {
      gridFields = 'stockItems';
    } else if (name === 'Stock Inventory' || name === 'Asset Inventory') {
      gridFields = 'inventories';
    } else if (name === 'Categories') {
      gridFields = 'categories';
    } else if (name === 'Location Types') {
      gridFields = 'locationTypes';
    } else if (name === 'facility') {
      gridFields = 'facility';
    } else if (name === 'Person') {
      gridFields = 'users';
    } else if (name === 'Vendor') {
      gridFields = 'vendors';
    } else if (name === 'Customer') {
      gridFields = 'customers';
    } else if (name === 'Purchase Order') {
      gridFields = 'purchaseOrders';
    } else if (name === 'Sales Order') {
      gridFields = 'salesOrders';
    } else if (name === 'Receive History') {
      gridFields = 'receiveHistory';
    } else if (name === 'Dispose To') {
      gridFields = 'disposeTo';
    } else if (typeof event === 'string') {
      gridFields = event;
    }

    if (gridFields) {
      this._gridFieldsService.getGridFieldsByPage(gridFields)
        .subscribe((x: any) => {
          this._getReportCategory(gridFields);
          this._gridFields = this._addBarcodeImageGridField(x);
        });
    }
  }

  getGridFieldsForEdit(event: any, selectedFields: string) {
    if (!event) return;
    let gridFields = event.pageName;
    if (!gridFields) {
      const name = typeof event === 'object' ? event.name : event;
      if (name === 'Location') gridFields = 'locations';
      else if (name === 'Stock Item' || name === 'Asset') gridFields = 'stockItems';
      else if (name === 'Stock Inventory' || name === 'Asset Inventory') gridFields = 'inventories';
      else if (name === 'Categories') gridFields = 'categories';
      else if (name === 'Location Types') gridFields = 'locationTypes';
      else if (name === 'facility') gridFields = 'facility';
      else if (name === 'Person') gridFields = 'users';
      else if (name === 'Vendor') gridFields = 'vendors';
      else if (name === 'Customer') gridFields = 'customers';
      else if (name === 'Purchase Order') gridFields = 'purchaseOrders';
      else if (name === 'Sales Order') gridFields = 'salesOrders';
      else if (name === 'Receive History') gridFields = 'receiveHistory';
      else if (name === 'Dispose To') gridFields = 'disposeTo';
      else if (typeof event === 'string') gridFields = event;
    }

    if (gridFields) {
      this._gridFieldsService.getGridFieldsByPage(gridFields).subscribe((x: any) => {
        this._getReportCategory(gridFields);
        let _gridFieldsFiltered = this._addBarcodeImageGridField(x || []);
        this._selectedgridFields = [];
        let nameFields: any[] = [];
        let barcodeFields: any[] = [];

        const preset = this.SharedModel?.labelPreset;
        if (preset?.nameField) {
          nameFields = preset.nameField.split(',');
        }
        if (preset?.barcodeField) {
          barcodeFields = preset.barcodeField.split(',');
        }

        const splitSelectedString = (selectedFields || '').split(',');
        splitSelectedString.forEach(obj => {
          if (nameFields.indexOf(obj) != -1) {
            let found = _gridFieldsFiltered.find((f: any) => f.field == obj && !f.title.includes('(QRCode image)'));
            if (found) {
              found.isSelected = false;
              this._selectedgridFields.push(found);
            }
          }
          if (barcodeFields.indexOf(obj) != -1) {
            let found = _gridFieldsFiltered.find((f: any) => f.field == obj && f.title.includes('(QRCode image)'));
            if (found) {
              found.isSelected = false;
              this._selectedgridFields.push(found);
            }
          }
        });

        if (this._selectedgridFields.length === 0 && splitSelectedString.length > 0) {
          splitSelectedString.forEach(obj => {
            let found = _gridFieldsFiltered.find((f: any) => f.field == obj);
            if (found) {
              this._selectedgridFields.push(found);
            }
          });
        }

        if (this._selectedgridFields && this._selectedgridFields.length > 0) {
          this._selectedgridFields.forEach((element) => {
            var findElement = _gridFieldsFiltered.find((f: any) => f.title == element.title);
            if (findElement) {
              _gridFieldsFiltered.splice(_gridFieldsFiltered.indexOf(findElement), 1);
            }
          });
        }

        this._gridFields = _gridFieldsFiltered;
        if (this.filterBy) {
          this.filterBy.value = this._selectedgridFields.find((f: any) => f.title == this._model.filterBy || f.field == this._model.filterBy);
          if (this.filterBy.value) {
            this._labelPresetObject.selectByField = (this.filterBy.value.page ? this.filterBy.value.page + '.' : '') + this.filterBy.value.field;
          }
        }
        this._previewBarcode();
      });
    }
  }

  _getReportCategories() {
    return this._reportService.AllCategories().subscribe(
      (x: any) => {
        this.reportCategories = x ? x.filter((cat: any) => cat.dataSource != null) : [];
      }
    );
  }

  _getReportCategory(code: string) {
    this.reportCategory = this.reportCategories?.find(x => x.reportCategorycode === code || x.pageName === code);
  }

  _addBarcodeImageGridField(gridFieldList: any[]) {
    if (!gridFieldList) return [];
    const list = [...gridFieldList];
    for (let i = 0; i < list.length; i++) {
      if (list[i]) {
        let imageField = Object.assign({}, list[i]);
        imageField.title += ' (QRCode image)';
        list.splice(i + 1, 0, imageField);
        i++;
      }
    }
    return list;
  }

  _loggedInAccountId: any;
  _loggedInSiteId: any;

  _loggedInSite() {
    const loggedInAccountSites = localStorage.getItem('loggedInAccountSites') ?? '';
    if (loggedInAccountSites !== '') {
      try {
        const parsed = JSON.parse(loggedInAccountSites)[0];
        this._loggedInAccountId = parsed.accountId;
        this._loggedInSiteId = parsed.siteId;
      } catch (e) {}
    }
  }

  getReportObject(): any {
    const dataSource = (typeof this._model.LabelPresetId === 'object' && this._model.LabelPresetId?.sourceView)
      ? this._model.LabelPresetId.sourceView
      : (this.reportCategory ? this.reportCategory.dataSource : null);

    const distinctFields = Array.from(new Set(
      (this._selectedgridFields || []).map((item: any) => item.field)
    )).join(',');

    return {
      name: this._model.name,
      isForecast: 0,
      systemReport: 0,
      siteId: this._loggedInSiteId,
      reportCategoryId: this.reportCategory ? this.reportCategory.reportCategoryid : null,
      dataSource: dataSource,
      addAddress: false,
      addLogo: false,
      groupBy: '',
      header: '',
      orientation: 'Portrait',
      selectedFields: distinctFields,
      definition: this.SharedModel?.labelPreset?.reportDefinition ?? '',
      filterBy: (this._labelPresetObject.filterBy && this._labelPresetObject.filterBy.includes('(QRCode image)'))
        ? (this._labelPresetObject.selectByField + '(QRCode image)')
        : (this._labelPresetObject.selectByField || ''),
      isQR: true,
      isURL: !!this._model.isURL
    };
  }

  _previewBarcode() {
    if (!this._selectedgridFields || this._selectedgridFields.length === 0) {
      return false;
    }

    if (this._isCustomSize) {
      if (!this._model.Width || !this._model.Height) {
        return false;
      }
      this.SetCustomSizeAttributes(false);
    }

    const reportObj: any = this.getReportObject();
    reportObj['isQR'] = true;
    reportObj['isQRCode'] = true;
    reportObj['isURL'] = !!this._model.isURL;

    this._model.isQR = true;
    this._model.isQr = true;
    this._model.isURL = !!this._model.isURL;

    this._previewObject = {
      ReportObj: reportObj,
      selectedFields: [...this._selectedgridFields]
    };
    this._model = { ...this._model };
    this._previewDate = true;
    this.cdr.detectChanges();
  }

  SetCustomSizeWidth(event: any) {
    this._model.Width = event;
  }

  SetCustomSizeHeight(event: any) {
    this._model.Height = event;
  }

  SetCustomSizeAttributes(FromView: boolean) {
    this._model.LabelSizeID = undefined;
    this._model.LabelSize = this._model.LabelSize || {};
    this._model.LabelSize.width = this._model.Width;
    this._model.LabelSize.height = this._model.Height;
    this._model.LabelSize.pageWidth = this._model.Width;
    this._model.LabelSize.pageHeight = this._model.Height;
    this._model.LabelSize.verticalPitch = 0;
    this._model.LabelSize.verticalMargin = 0;
    this._model.LabelSize.horizontalMargin = 0;
    this._model.LabelSize.pageLeftMargin = 0;
    this._model.LabelSize.pageBottomMargin = 0;
    this._model.LabelSize.pageRightMargin = 0;
    this._model.LabelSize.pageTopMargin = 0;
    this._model.LabelSize.columns = 1;
    this._model.LabelSize.rows = 1;
    this._model.Row = 1;
    this._model.Column = 1;

    if (FromView) {
      this.currentStep = 1;
      this._getGridFields(this._gridFieldName);
    }
  }

  _nameChange(event: any) {
    if (event) {
      this.labelType.disabled = false;
    } else {
      this.labelType.disabled = true;
      this._showSave = false;
    }
  }

  catalogNameChange() {
    if (!this.labelType.disabled) {
      setTimeout(() => {
        this.labelType.focus();
        this.labelType.toggle();
      }, 200);
    }
  }

  _customSizeChange(event: string) {
    if (event === 'Yes') {
      setTimeout(() => {
        this._isCustomSize = true;
        this._model.Width = undefined;
        this._model.Height = undefined;
        this._model.LabelSize = {};
        this._getGridFields(this._gridFieldName);
      }, 200);
    } else {
      setTimeout(() => {
        this._isCustomSize = false;
        this._getLabelSize();
      }, 200);
    }
  }

  _filterByChange(event: any) {
    if (event) {
      this._labelPresetObject.filterBy = event.title;
      this._labelPresetObject.selectByField = event.page ? (event.page + '.' + event.field) : event.field;
      this._showSave = true;
      this.currentStep = 4;
      this._previewBarcode();
    } else {
      this._showSave = false;
    }
  }

  _onSave(event: string) {
    if (!this._model.name) {
      this._layoutComponent.openPopup({
        title: 'Validation',
        message: 'Please enter Name',
        withoutConfirm: true
      });
      return false;
    }

    if (!this._selectedgridFields || this._selectedgridFields.length === 0) {
      this._layoutComponent.openPopup({
        title: 'Validation',
        message: 'Please select at least one field',
        withoutConfirm: true
      });
      return false;
    }

    if (this._isCustomSize) {
      if (!this._model.Width) {
        this._layoutComponent.openPopup({
          title: 'Validation',
          message: 'Please enter width',
          withoutConfirm: true
        });
        return false;
      }

      if (!this._model.Height) {
        this._layoutComponent.openPopup({
          title: 'Validation',
          message: 'Please enter height',
          withoutConfirm: true
        });
        return false;
      }
    }

    this._labelPresetObject.name = this._model.name;
    this._labelPresetObject.sourceView = this._previewObject?.ReportObj?.dataSource;
    this._labelPresetObject.barcodeField = '';
    this._labelPresetObject.nameField = '';

    if (this._previewObject?.selectedFields) {
      let barcodeFields: string[] = [];
      let nameFields: string[] = [];
      this._previewObject.selectedFields.forEach((element: any) => {
        if (element.title && element.title.includes('(QRCode image)')) {
          barcodeFields.push(element.field);
        } else {
          nameFields.push(element.field);
        }
      });
      this._labelPresetObject.barcodeField = barcodeFields.join(',');
      this._labelPresetObject.nameField = nameFields.join(',');
    }

    this._labelPresetObject.catalog = false;
    this._labelPresetObject.custom = true;
    this._labelPresetObject.twoDimensional = false;
    this._labelPresetObject.rows = this._model.Row;
    this._labelPresetObject.columns = this._model.Column;
    this._labelPresetObject.isPortrait = true;
    this._labelPresetObject.assetrac = true;
    this._labelPresetObject.instock = true;
    this._labelPresetObject.isAsset = this.isAsset;
    this._labelPresetObject.customLabel = true;
    this._labelPresetObject.labelSizeID = this._model.LabelSizeID;
    this._labelPresetObject.selectedFields = this._previewObject?.ReportObj?.selectedFields;
    this._labelPresetObject.isBarcode = false;
    this._labelPresetObject.isQRCode = true;
    this._labelPresetObject.isURL = !!this._model.isURL;
    this._labelPresetObject.Width = this._model.Width;
    this._labelPresetObject.Height = this._model.Height;
    this._labelPresetObject.labelType = LabelType.PublicQr;

    if (this._model.LabelPresetId) {
      this._labelPresetObject.relatedLabelPresetID = this._model.LabelPresetId.id;
      this._labelPresetObject.pageName = this._model.LabelPresetId.pageName;
    }

    if (this._pageState === PageState.EDIT && this._model.id) {
      this._labelPresetObject.id = this._model.id;
      this._labelService.updateLabelPreset(this._model.id, this._labelPresetObject).subscribe((response: any) => {
        if (response != null && response['message'] != null) {
          this._layoutComponent.openPopup({
            title: 'Alert',
            message: response['message'],
            withoutConfirm: true
          });
          this._labelPresetObject.id = this._model.id;
          this._labelPresetObject.labelType = LabelType.PublicQr;
          this._labelService.labelPrintUpdated.emit(this._labelPresetObject);
          if (event === 'saveAndClose') {
            this._closeForm();
          } else if (event === 'saveAndCopy') {
            this._model.id = null;
            this._model.name = null;
            delete this._labelPresetObject.id;
            this._pageState = PageState.ADD;
          } else if (event === 'saveAndNew') {
            this._model.id = null;
            this._model.name = null;
            this._model.LabelPresetId = undefined;
            if (this.labelSize) this.labelSize.value = undefined;
            if (this.labelType) this.labelType.value = undefined;
            if (this.customSize) this.customSize.value = undefined;
            if (this.filterBy) this.filterBy.value = undefined;
            this._gridFields = [];
            this._selectedgridFields = [];
            this._previewDate = false;
            this._previewObject = undefined;
            delete this._labelPresetObject.id;
            this._pageState = PageState.ADD;
          } else if (event === 'saveAndLC' || event === 'saveAndLF' || event === 'saveAndEL') {
            this._closeForm();
            this.openDesigner(this._model.id);
          }
        }
      });
    } else {
      this._labelService.addLabelPreset(this._labelPresetObject)
        .subscribe((response: any) => {
          if (response != null && response['message'] != null) {
            this._layoutComponent.openPopup({
              title: 'Alert',
              message: response['message'],
              withoutConfirm: true
            });
            const newId = response.responseId ?? response.data?.responseId ?? response.id;
            this._labelPresetObject.id = newId;
            this._labelPresetObject.labelType = LabelType.PublicQr;
            this._labelService.labelAdded.emit(this._labelPresetObject);
            if (event === 'saveAndClose') {
              this._closeForm();
            } else if (event === 'saveAndCopy') {
              this._model.id = null;
              this._model.name = null;
              delete this._labelPresetObject.id;
              this._pageState = PageState.ADD;
            } else if (event === 'saveAndNew') {
              this._model.id = null;
              this._model.name = null;
              this._model.LabelPresetId = undefined;
              if (this.labelSize) this.labelSize.value = undefined;
              if (this.labelType) this.labelType.value = undefined;
              if (this.customSize) this.customSize.value = undefined;
              if (this.filterBy) this.filterBy.value = undefined;
              this._gridFields = [];
              this._selectedgridFields = [];
              this._previewDate = false;
              this._previewObject = undefined;
              delete this._labelPresetObject.id;
              this._pageState = PageState.ADD;
            } else if (event === 'saveAndLC' || event === 'saveAndLF' || event === 'saveAndEL') {
              this._closeForm();
              this.openDesigner(newId);
            }
          }
        });
    }
  }

  openDesigner(reportId: number) {
    this._layoutComponent.openDialogForm(ReportDesignerComponent, PageState.EDIT, { reportId: reportId, IsBarcodeLabelLayout: true });
  }

  _qrDefinition(event: any) {
    this._labelPresetObject.reportDefinition = event;
  }
}
